/**
 * Pricing page component for Heapster ID subscription plans
 * Shows each plan's name and price, and starts Stripe Checkout
 * Fetches dynamic pricing from backend API
 *
 * It lists no features (#2209): the product's `features` and `highlights`,
 * which the backend forwards from Stripe product metadata, are not rendered.
 *
 * The HeapsterLive card offers Subscribe only to someone known not to be
 * subscribed (#2218). A subscriber gets the billing portal instead, and
 * Subscribe stays disabled while the state loads or when it cannot be read.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  usePurchase,
  useProducts,
  useSubscription,
  useBillingPortal,
} from '../services/subscription';
import { selectPriceForCurrency } from '../services/subscription/price';
import {
  CHECKOUT_START_FAILED_MESSAGE,
  checkoutOutcomeFor,
  heapsterLiveCheckoutState,
  type CheckoutOutcome,
} from '../services/subscription/checkoutState';
import {
  ALREADY_HAVE_ACCESS_MESSAGE,
  CHECKOUT_CONFLICT_MESSAGES,
  MANAGE_SUBSCRIPTION_LABEL,
  SUBSCRIPTION_LOAD_ERROR_MESSAGE,
  purchaseConflictReason,
} from '../lib/checkoutConflict';
import { LoadingSpinner } from '../components/ui';

interface PricingTier {
  id: string;
  name: string;
  price: number; // Price in smallest currency unit (cents)
  priceId: string;
  interval: string;
  currency: string;
  highlighted: boolean;
}

export function PricingPage() {
  const navigate = useNavigate();
  const { user, isEmailVerified, isLoading: authLoading } = useAuth();
  const { purchase, isProcessing } = usePurchase();
  const {
    products,
    detectedCurrency,
    isLoading: productsLoading,
    error: productsError,
  } = useProducts();
  const {
    subscription,
    isLoading: subscriptionLoading,
    error: subscriptionError,
  } = useSubscription();
  const { openPortal, isOpening: portalOpening } = useBillingPortal();

  // What the last checkout attempt came to, kept with the uid that made it and
  // shown only to that user.
  const [lastCheckout, setLastCheckout] = useState<CheckoutOutcome | null>(
    null
  );
  const checkoutOutcome = checkoutOutcomeFor(lastCheckout, user?.uid);
  const purchaseError = checkoutOutcome?.error ?? '';
  // Set when checkout was refused because a HeapsterLive subscription already
  // exists (the backend's 409). The card then offers the billing portal.
  const checkoutConflict = checkoutOutcome?.conflict ?? null;

  const checkoutState = heapsterLiveCheckoutState({
    signedIn: !!user,
    isLoading: subscriptionLoading,
    error: subscriptionError,
    subscription,
  });

  // Currency symbol mapping
  const getCurrencySymbol = (currency: string): string => {
    const symbols: Record<string, string> = {
      usd: '$',
      gbp: '£',
    };
    return symbols[currency.toLowerCase()] || '$';
  };

  // Build pricing tiers from backend products
  const buildPricingTiers = (): PricingTier[] => {
    const tiers: PricingTier[] = [];

    // Add HeapsterLive product from backend
    if (products.length > 0) {
      const heapsterLiveProduct = products[0]; // Currently only one product

      // Same rule as the subscription management page, and the same code —
      // this page had it right inline and that page had it wrong, which is the
      // shape of divergence one shared author prevents (#1135).
      const price = selectPriceForCurrency(
        heapsterLiveProduct.prices,
        detectedCurrency
      );

      if (price) {
        tiers.push({
          id: heapsterLiveProduct.productId,
          name: heapsterLiveProduct.name,
          price: price.amount,
          priceId: price.priceId,
          interval: price.interval,
          currency: price.currency,
          highlighted: true,
        });
      }
    }

    return tiers;
  };

  const pricingTiers = buildPricingTiers();

  // Handle plan selection
  const handlePlanSelect = async (tier: PricingTier) => {
    if (!user) {
      navigate('/signin', {
        state: { from: '/pricing' },
      });
      return;
    }

    if (!isEmailVerified) {
      navigate('/verify-email', {
        state: { from: '/pricing' },
      });
      return;
    }

    const { uid } = user;
    try {
      setLastCheckout(null);
      // Use priceId directly from the tier
      await purchase(tier.priceId);
    } catch (error) {
      console.error('Purchase error:', error);
      const conflict = purchaseConflictReason(error);
      if (conflict) {
        setLastCheckout({ uid, conflict });
        return;
      }
      setLastCheckout({
        uid,
        error:
          error instanceof Error
            ? error.message
            : CHECKOUT_START_FAILED_MESSAGE,
      });
    }
  };

  const planButtonClass = (tier: PricingTier, disabled: boolean) =>
    `w-full py-3 px-4 rounded-lg font-medium transition-colors ${
      tier.highlighted
        ? 'bg-blue-600 hover:bg-blue-700 text-white'
        : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-900 dark:text-white'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`;

  // The button that selects a plan. `loading` and a purchase in progress
  // disable it and replace its label; `blocked` only disables it.
  const renderPlanButton = (
    tier: PricingTier,
    label: string,
    { loading, blocked = false }: { loading: boolean; blocked?: boolean }
  ) => {
    const disabled = loading || isProcessing || blocked;
    return (
      <button
        onClick={() => handlePlanSelect(tier)}
        disabled={disabled}
        className={`mt-6 ${planButtonClass(tier, disabled)}`}
      >
        {loading ? (
          <>
            <LoadingSpinner size="sm" className="mr-2" />
            Loading...
          </>
        ) : isProcessing ? (
          <>
            <LoadingSpinner size="sm" className="mr-2" />
            Processing...
          </>
        ) : (
          label
        )}
      </button>
    );
  };

  // What the HeapsterLive card offers (#2218). Subscribe only when the
  // person is known not to be subscribed, or is signed out and will be sent
  // to sign in.
  const renderHeapsterLiveAction = (tier: PricingTier) => {
    // A subscriber, or someone the backend has just refused as one: the
    // billing portal, where both an existing subscription and a payment
    // method that needs updating are dealt with.
    if (checkoutConflict || checkoutState === 'subscribed') {
      return (
        <>
          {checkoutConflict && (
            <p className="mt-6 text-sm text-gray-700 dark:text-gray-300">
              {CHECKOUT_CONFLICT_MESSAGES[checkoutConflict]}
            </p>
          )}
          <button
            onClick={() => void openPortal()}
            disabled={portalOpening}
            className={`${checkoutConflict ? 'mt-3' : 'mt-6'} ${planButtonClass(tier, portalOpening)}`}
          >
            {MANAGE_SUBSCRIPTION_LABEL}
          </button>
        </>
      );
    }

    // Access that Stripe does not bill (Supapass, a manual grant): nothing
    // to subscribe to and nothing in the portal to manage.
    if (checkoutState === 'access-without-subscription') {
      return (
        <p className="mt-6 text-sm text-gray-700 dark:text-gray-300">
          {ALREADY_HAVE_ACCESS_MESSAGE}
        </p>
      );
    }

    return (
      <>
        {renderPlanButton(tier, 'Subscribe Now', {
          loading: authLoading || checkoutState === 'loading',
          blocked: checkoutState === 'error',
        })}
        {checkoutState === 'error' && (
          <p className="mt-3 text-sm text-red-600 dark:text-red-400">
            {SUBSCRIPTION_LOAD_ERROR_MESSAGE}
          </p>
        )}
      </>
    );
  };

  // Show loading state
  if (productsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Show error state
  if (productsError || pricingTiers.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">
            Failed to load pricing information. Please try again later.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <button
                onClick={() => navigate(-1)}
                className="mr-4 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
              </button>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Choose Your Plan
              </h1>
            </div>
            {user && (
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Signed in as {user.display_name}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Purchase Error Display */}
        {purchaseError && (
          <div className="max-w-md mx-auto mb-8">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 text-red-600 dark:text-red-400 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <p className="text-sm text-red-800 dark:text-red-200 font-medium">
                    Payment Error
                  </p>
                  <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                    {purchaseError}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 gap-6 max-w-md mx-auto">
          {pricingTiers.map(tier => {
            const finalPrice = tier.price / 100; // Convert cents to dollars

            return (
              <div
                key={tier.id}
                className={`rounded-lg border-2 p-6 ${
                  tier.highlighted
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                }`}
              >
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {tier.name}
                  </h3>

                  <div className="mt-4">
                    <div className="flex items-center justify-center">
                      <span className="text-3xl font-bold text-gray-900 dark:text-white">
                        {getCurrencySymbol(tier.currency)}
                        {finalPrice.toFixed(2)}
                      </span>
                      <span className="ml-1 text-sm text-gray-500 dark:text-gray-400">
                        /{tier.interval}
                      </span>
                    </div>
                  </div>

                  {renderHeapsterLiveAction(tier)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
